import {useEffect, useMemo, useState} from 'react';
import * as css from './SeatmapManage.css';
import {ARMCHAIR_ICON, HANDSHAKE_ICON} from './seatIcons';
import useMobile from '../../../hooks/useMobile';

// The LEGACY seat editor's slide-in menu (.edit-seat), cloned onto the native
// manager: selection counters + summary, the stateful action items, the
// per-selection ticket list with the ✓/! badges, and the partner (שיריונים)
// panel — same terminology, same flows.

// legacy change_title_text(): human summary of the selection
export function selectionTitle(selectedSeats, seats, areas) {
    if(!selectedSeats.length) return '';
    const smaids = new Set(selectedSeats.map((s) => s.smaid));
    if(smaids.size === 1) {
        const smaid = selectedSeats[0].smaid;
        const gushSeats = seats.filter((s) => s.smaid === smaid && s.state !== 'sold');
        if(selectedSeats.length >= gushSeats.length && gushSeats.length > 0) {
            const a = (areas || []).find((x) => x.smaid === smaid);
            return 'גוש: ' + (a?.title || '');
        }
    }
    const rows = new Set(selectedSeats.map((s) => s.smaid + '|' + (s.row ?? '')));
    if(rows.size > 1) return 'מושבים: mix';
    const row = selectedSeats[0].row;
    if(selectedSeats.length === 1) return `שורה ${row}, מושב ${selectedSeats[0].seat}`;
    // ranges like "1-4,7" (legacy cuts at 9 chars + '...')
    const nums = selectedSeats.map((s) => parseInt(s.seat, 10)).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
    const parts = [];
    for(let i = 0; i < nums.length; i++) {
        let j = i;
        while(j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j++;
        parts.push(j > i ? `${nums[i]}-${nums[j]}` : String(nums[i]));
        i = j;
    }
    let txt = parts.join(',');
    if(txt.length > 9) txt = txt.slice(0, 9) + '...';
    return `שורה ${row}, מושבים: ${txt}`;
}

// legacy counters: rows counted only when ENTIRE rows are selected
export function selectionCounts(selectedSeats, seats) {
    const byRow = new Map();
    for(const s of seats) {
        if(s.state === 'sold') continue;
        const k = s.smaid + '|' + (s.row ?? '');
        const e = byRow.get(k) || {total: 0, sel: 0};
        e.total++;
        byRow.set(k, e);
    }
    for(const s of selectedSeats) {
        const e = byRow.get(s.smaid + '|' + (s.row ?? ''));
        if(e) e.sel++;
    }
    let rows = 0;
    for(const e of byRow.values()) if(e.total > 0 && e.sel >= e.total) rows++;
    return {rows, seats: selectedSeats.length};
}

export function Confirm({title, text, okText, cancelText, onOk, onCancel}) {
    return (
        <css.confirmWrap onClick={onCancel}>
            <css.confirmBox onClick={(e) => e.stopPropagation()}>
                <div className="title">{title}</div>
                <div className="text">{text}</div>
                <div className="btns">
                    <button onClick={onCancel}>{cancelText}</button>
                    <button className="ok" onClick={onOk}>{okText}</button>
                </div>
            </css.confirmBox>
        </css.confirmWrap>
    );
}

function EditSeatMenu({
    show, selectedSeats, seats, areas, tickets, partners, eid,
    onClose, onSelectAll, onClearSelection,
    onToggleBlocked, onToggleHidden, onOpenAssign,
    onReserveToPartner, onUnreserve, onCreatePartner, onDeleteTicket,
    onSelectPartnerSeats, busy
}) {
    const [partnerOpen, setPartnerOpen] = useState(false);
    const [newPartner, setNewPartner] = useState(null); // null=closed; {title,phone,contactName,contactEmail,contactPhone}
    const [confirm, setConfirm] = useState(null);
    const mobile = useMobile();
    // mobile: the menu first opens as a slim rail so the map stays visible; the arrow expands it
    const [expanded, setExpanded] = useState(true);
    useEffect(() => { if(show) setExpanded(!mobile); }, [show, mobile]);

    const counts = useMemo(() => selectionCounts(selectedSeats, seats), [selectedSeats, seats]);
    const title = useMemo(() => selectionTitle(selectedSeats, seats, areas), [selectedSeats, seats, areas]);

    // which שיריונים the selected reserved seats belong to
    const reservedInfo = useMemo(() => {
        const per = new Map();
        for(const s of selectedSeats) {
            if(s.state !== 'reserved' || !s.smpid) continue;
            per.set(s.smpid, (per.get(s.smpid) || 0) + 1);
        }
        return [...per.entries()].map(([smpid, sel]) => ({
            smpid, sel,
            title: (partners.find((p) => p.smpid === Number(smpid)) || {}).title || '',
            total: seats.filter((x) => x.smpid === smpid && x.state === 'reserved').length,
        }));
    }, [selectedSeats, seats, partners]);

    const nonReserved = selectedSeats.filter((s) => s.state !== 'reserved');
    const allBlocked = nonReserved.length > 0 && nonReserved.every((s) => s.state === 'blocked');
    const allHidden = nonReserved.length > 0 && nonReserved.every((s) => s.state === 'hidden');
    const anyReserved = selectedSeats.some((s) => s.state === 'reserved');

    // per-ticket counters over the selection (legacy update_list_tickets)
    const ticketRows = useMemo(() => {
        const counter = {};
        for(const s of selectedSeats) for(const tid of s.tids || []) counter[tid] = (counter[tid] || 0) + 1;
        return tickets
            .map((t) => ({...t, counter: counter[t.tid] || 0, action: (counter[t.tid] || 0) === selectedSeats.length && selectedSeats.length > 0 ? 'ti-all' : (counter[t.tid] || 0) > 0 ? 'ti-conflict' : 'ti-no'}))
            .filter((t) => t.action !== 'ti-no');
    }, [tickets, selectedSeats]);
    const multiple = ticketRows.length > 1;

    const askDeleteTicket = (t) => {
        const moreText = t.action === 'ti-conflict' ? ' מהמושבים שנבחרו.' : ` מ - ${t.counter} מושבים.`;
        // would some seats stay ticketless? a seat whose ONLY ticket is t
        const wouldStrip = selectedSeats.some((s) => (s.tids || []).includes(t.tid) && (s.tids || []).length <= 1);
        if(wouldStrip) {
            setConfirm({
                title: 'אין מושב בלי כרטיס...',
                text: `אתם הולכים למחוק את כרטיס ${t.title}${moreText}\nשימו לב! אם ישנו מושב ללא כרטיס נוסף, הכרטיס לא ימחק.`,
                okText: 'מודע לכך', cancelText: 'אבדוק שוב',
                onOk: () => { setConfirm(null); onDeleteTicket(t.tid); }
            });
        } else {
            setConfirm({
                title: 'הסרת כרטיס!',
                text: `אתם הולכים למחוק את כרטיס ${t.title}${moreText}`,
                okText: 'בהחלט', cancelText: 'לא התכוונתי',
                onOk: () => { setConfirm(null); onDeleteTicket(t.tid); }
            });
        }
    };

    const askUnreserve = () => {
        const perPartner = {};
        for(const s of selectedSeats) {
            if(s.state !== 'reserved' || !s.smpid) continue;
            perPartner[s.smpid] = (perPartner[s.smpid] || 0) + 1;
        }
        const lines = Object.entries(perPartner)
            .map(([smpid, n]) => `${n} מקומות של ${(partners.find((p) => p.smpid === Number(smpid)) || {}).title || ''}`)
            .join(', ');
        setConfirm({
            title: 'מקומות משוריינים!',
            text: `שימו לב! אתם הולכים להסיר שריון מ: ${lines}`,
            okText: 'בהחלט', cancelText: 'ביטול',
            onOk: () => { setConfirm(null); onUnreserve(); }
        });
    };

    const submitNewPartner = async () => {
        if(!(newPartner?.title || '').trim()) return;
        const p = await onCreatePartner(newPartner);
        if(p?.smpid) {
            setNewPartner(null);
            onReserveToPartner(p.smpid);
            setPartnerOpen(false);
        }
    };

    if(show && !expanded) {
        return (
            <css.editMenu className="show slim">
                <css.emRail onClick={() => setExpanded(true)} title="הרחבת התפריט">
                    {/* the menu is direction:rtl and ❮/❯ are bidi-MIRRORED there — chars are visually swapped */}
                    <span className="arrow">❮</span>
                    <span className="cnt">
                        {counts.rows > 0 ? `בחרתם ${counts.rows} שורות, ${counts.seats} מושבים` : `בחרתם ${counts.seats} מושבים`}
                    </span>
                </css.emRail>
            </css.editMenu>
        );
    }

    return (
        <css.editMenu className={show ? 'show' : ''}>
            {mobile && show &&
                <css.emTab onClick={() => setExpanded(false)} title="כיווץ התפריט">❯</css.emTab>}
            <css.emClose onClick={onClose}>✕</css.emClose>
            <css.emToolbox>
                <button onClick={onSelectAll}>בחר הכל</button>
                <button onClick={onClearSelection}>נקה בחירה</button>
            </css.emToolbox>
            <css.emTitle>
                <div className="count">
                    {counts.rows > 0 ? `בחרתם ${counts.rows} שורות, ${counts.seats} מושבים` : `בחרתם ${counts.seats} מושבים`}
                </div>
                <div className="txt">{title}</div>
            </css.emTitle>

            {reservedInfo.length > 0 &&
                <css.emReserved>
                    <div className="rTitle">{reservedInfo.length === 1 ? 'שריון בבחירה' : 'שריונים בבחירה'}</div>
                    {reservedInfo.map((r) => (
                        <div className="rRow" key={r.smpid}>
                            <img src={HANDSHAKE_ICON} alt="" style={{filter: 'invert(0.4)'}} />
                            <span className="rName">
                                <a href={`${window.location.origin}/e/${eid}/tickets/reserved?id=${r.smpid}`}
                                    target="_blank" rel="noreferrer" title="לפרטי השריון">{r.title}</a>
                            </span>
                            <span className="rCnt">{r.sel}/{r.total}</span>
                            <span className="rSel" onClick={() => !busy && onSelectPartnerSeats && onSelectPartnerSeats(r.smpid)}>
                                בחר את כל השריון
                            </span>
                        </div>
                    ))}
                </css.emReserved>}

            <css.emItem onClick={() => !busy && onToggleBlocked(allBlocked)}>
                <span className="ic"><img src={ARMCHAIR_ICON} alt="" style={{filter: 'invert(0.5)'}} /></span>
                {allBlocked ? 'שחרר למכירה' : 'הצג כנמכר'}
            </css.emItem>
            <css.emItem onClick={() => { if(busy) return; anyReserved ? askUnreserve() : setPartnerOpen(true); }}>
                <span className="ic"><img src={HANDSHAKE_ICON} alt="" style={{filter: 'invert(0.5)'}} /></span>
                {anyReserved ? 'הסר שריון' : 'שריין לשותף'}
            </css.emItem>
            <css.emItem onClick={() => !busy && onToggleHidden(allHidden)}>
                <span className="ic">👁</span>
                {allHidden ? 'הצג מושב' : 'הסתר מושב'}
            </css.emItem>
            <css.emItem onClick={() => !busy && onOpenAssign('replace')}>
                <span className="ic">🎫</span>
                שינוי כרטיס למושב
            </css.emItem>
            <css.emItem onClick={() => !busy && onOpenAssign('add')}>
                <span className="ic">🎫+</span>
                מושב מרובה כרטיסים
            </css.emItem>

            <css.emTickets>
                {ticketRows.map((t) => (
                    <css.emTicketRow key={t.tid}>
                        <css.emSwatch $color={`#${(t.color || '').replace('#', '') || '93c5fd'}`} $deletable={multiple}
                            title={multiple ? 'הסרת הכרטיס מהמושבים' : undefined}
                            onClick={() => multiple && !busy && askDeleteTicket(t)}>
                            <img src={ARMCHAIR_ICON} alt="" />
                            <span className="trash">🗑</span>
                            <css.emBadge $all={t.action === 'ti-all'}
                                title={t.action === 'ti-all' ? 'כרטיס זה משוייך לכל מהמושבים' : 'כרטיס זה משוייך לחלק מהמושבים'}>
                                {t.action === 'ti-all' ? '✓' : '!'}
                            </css.emBadge>
                        </css.emSwatch>
                        <span className="title">{t.title}</span>
                        <span className="price">₪{t.price}</span>
                    </css.emTicketRow>
                ))}
            </css.emTickets>

            {partnerOpen &&
                <css.partnerPanel>
                    <css.emClose onClick={() => { setPartnerOpen(false); setNewPartner(null); }}>✕</css.emClose>
                    <div className="pTitle">{partners.length ? 'השותפים שלנו' : 'הוסיפו את השותף הראשון שלכם'}</div>
                    <div className="pNew">
                        <button onClick={() => setNewPartner(newPartner ? null : {title: '', phone: '', contactName: '', contactEmail: '', contactPhone: ''})}>+ חדש</button>
                    </div>
                    {newPartner &&
                        <css.partnerForm>
                            <input autoFocus placeholder="שם השותף" value={newPartner.title}
                                onChange={(e) => setNewPartner({...newPartner, title: e.target.value})} />
                            <input placeholder="טלפון" value={newPartner.phone}
                                onChange={(e) => setNewPartner({...newPartner, phone: e.target.value})} />
                            <input placeholder="שם איש קשר" value={newPartner.contactName}
                                onChange={(e) => setNewPartner({...newPartner, contactName: e.target.value})} />
                            <input placeholder="אימייל" value={newPartner.contactEmail}
                                onChange={(e) => setNewPartner({...newPartner, contactEmail: e.target.value})} />
                            <input placeholder="טלפון איש קשר" value={newPartner.contactPhone}
                                onChange={(e) => setNewPartner({...newPartner, contactPhone: e.target.value})} />
                            <div className="row">
                                <css.assignBtn className="primary" disabled={busy || !(newPartner.title || '').trim()} onClick={submitNewPartner}>שמור</css.assignBtn>
                            </div>
                        </css.partnerForm>}
                    <div className="pList">
                        {partners.map((p) => (
                            <css.partnerRow key={p.smpid} onClick={() => { if(!busy) { onReserveToPartner(p.smpid); setPartnerOpen(false); } }}>
                                <img src={HANDSHAKE_ICON} alt="" style={{filter: 'invert(0.4)'}} />
                                <span>{p.title}</span>
                            </css.partnerRow>
                        ))}
                    </div>
                </css.partnerPanel>}

            {confirm &&
                <Confirm {...confirm} onCancel={() => setConfirm(null)} />}
        </css.editMenu>
    );
}

export default EditSeatMenu;
